// A product's own tags, as the support desk's tag editor has them (aldoo-ra Admin/CustomerSupport/TagDialog):
// an input whose suggestions come as it is typed — the tags other products carry and the product type's
// keywords (`book {productTagSuggestions}`) — Enter, a comma or Add puts one in, and the tags are coloured
// chips, each with its ×. 1–40 characters, 30 at most, each once (ignoring case).
import React, { useEffect, useRef, useState } from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import Popover from "aldoo-ra/Popover"
import { TextInput } from "../ui"
import { ProductTagSuggestions } from "../api"

const MAX_TAGS = 30
const COLORS = ["bg-blue-100 text-blue-700", "bg-green-100 text-green-700", "bg-purple-100 text-purple-700", "bg-orange-100 text-orange-700", "bg-pink-100 text-pink-700", "bg-teal-100 text-teal-700", "bg-yellow-100 text-yellow-800", "bg-red-100 text-red-700"]

/** A tag's colour, the same every time it shows. */
const colorOf = (tag) => {
  let hash = 0
  for (let i = 0; i < tag.length; i++) hash = (hash * 31 + tag.charCodeAt(i)) | 0
  return COLORS[Math.abs(hash) % COLORS.length]
}

/** `value`: the tags; `onChange(tags)`; `productType`: whose keywords are offered first; `t`: the host's texts. */
export default function TagsEditor({ value = [], onChange, productType = "", t = (s) => s, disabled = false }) {
  const tags = Array.isArray(value) ? value : []
  const [draft, setDraft] = useState("")
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(-1)
  // the input the suggestions hang under: they open above the page (aldoo-ra Popover), so a dialog's
  // scrolling body does not cut them off
  const anchor = useRef(null)

  // the suggestions follow the typing (a moment after it settles)
  useEffect(() => {
    if (!open) return
    const timer = setTimeout(() => {
      ProductTagSuggestions(draft.trim(), productType)
        .then((res) => {
          const have = new Set(tags.map((tag) => tag.toLowerCase()))
          setSuggestions((res?.list || []).filter((tag) => !have.has(String(tag).toLowerCase())).slice(0, 10))
          setActive(-1)
        })
        .catch(() => setSuggestions([]))
    }, 200)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, open, productType, tags.length])

  const add = (text) => {
    const tag = String(text || "").replace(/\s+/g, " ").trim()
    setDraft("")
    if (!tag || tag.length > 40 || /[<>]/.test(tag) || tags.length >= MAX_TAGS) return
    if (tags.some((one) => one.toLowerCase() === tag.toLowerCase())) return
    onChange?.([...tags, tag])
  }
  const remove = (tag) => onChange?.(tags.filter((one) => one !== tag))

  const onKeyDown = (event) => {
    if (event.key === "ArrowDown" && suggestions.length) {
      event.preventDefault()
      setOpen(true)
      setActive((index) => (index + 1) % suggestions.length)
    } else if (event.key === "ArrowUp" && suggestions.length) {
      event.preventDefault()
      setActive((index) => (index <= 0 ? suggestions.length - 1 : index - 1))
    } else if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      add(active >= 0 && suggestions[active] ? suggestions[active] : draft)
    } else if (event.key === "Backspace" && !draft && tags.length) {
      remove(tags[tags.length - 1])
    } else if (event.key === "Escape") {
      setOpen(false)
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid="tags-editor">
      <div className="relative" ref={anchor}>
        <TextInput
          value={draft}
          onChange={(text) => {
            setDraft(text)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          maxLength={40}
          disabled={disabled || tags.length >= MAX_TAGS}
          placeholder={tags.length >= MAX_TAGS ? t("%s tags at most", MAX_TAGS) : t`Add a tag`}
          className="pr-20"
          data-testid="tags-editor-input"
        />
        {draft.trim() && (
          <Button type="button" variant="admin_primary" onClick={() => add(draft)} className="absolute right-1 top-1/2 -translate-y-1/2 h-8 px-3 text-xs">
            {t`Add`}
          </Button>
        )}
        {/* a click outside (not on the input) closes them */}
        <Popover open={open && suggestions.length > 0} onClose={() => setOpen(false)} anchorRef={anchor} offset={4} className="rounded-md border-admin_bordered bg-admin_paper shadow-lg">
          <ul style={{ width: anchor.current?.offsetWidth }} className="m-0 p-1 list-none max-h-[220px] overflow-y-auto" role="listbox" data-testid="tags-editor-suggestions">
            {suggestions.map((tag, index) => (
              <li key={tag}>
                <button
                  type="button"
                  role="option"
                  aria-selected={index === active}
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => add(tag)}
                  className={`w-full text-left px-3 py-1.5 rounded text-sm ${index === active ? "bg-admin_primary/10 text-admin_primary" : "hover:bg-admin_neutral_1"}`}
                >
                  {tag}
                </button>
              </li>
            ))}
          </ul>
        </Popover>
      </div>
      <div className="flex flex-wrap gap-2 min-h-[32px]" data-testid="tags-editor-chips">
        {tags.map((tag) => (
          <span key={tag} className={`inline-flex items-center h-7 pl-3 pr-2 rounded-full text-sm ${colorOf(tag)}`}>
            {tag}
            <button type="button" onClick={() => remove(tag)} disabled={disabled} className="ml-1.5 hover:opacity-70" aria-label={t`Remove`}>
              <XMarkIcon className="w-4 h-4 stroke-2" />
            </button>
          </span>
        ))}
        {!tags.length && <span className="text-sm text-admin_text_secondary italic self-center">{t`No tags yet`}</span>}
      </div>
    </div>
  )
}
